import _ from "lodash";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import { useI18n, useFetchQuery } from "Hooks";
import { Alert, Avatar, Button, Modal, RadioButton } from "@toddle-edu/ds-web";

import {
  getChangeTaskTypeOptions,
  getCheckBoxAlertMessage,
  getShowCheckboxAlertMessage,
  getShowNoteValue,
  getTemplateIdFromOrgId,
} from "ClassRoom/modules/utils";
import { getAssessmentLinkedUnitQuery } from "ClassRoom/modules/ClassRoomQuery";
import {
  getCurrentCurriculumProgramType,
  getOrganizationId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { getCurrentAssessmentTypes } from "Platform/modules/PlatformModule";
import {
  changeAssessmentType,
  getAssessmentObj,
} from "ClassRoom/modules/ClassRoomModule";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import Loading from "UIComponents/Loading";
import {
  ASSESSMENT,
  ASSESSMENT_TYPE_QUICK_TASK,
  GROUP_TYPE_WORKSHEET,
  LEARNING_ENGAGEMENT,
} from "Constants/stringConstants";
import { colors } from "Constants";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";

import classes from "./ChangeTaskType.scss";

const getTypeEnum = type => {
  switch (type) {
    case "qt":
      return ASSESSMENT_TYPE_QUICK_TASK;
    case "le":
      return LEARNING_ENGAGEMENT;
    case "worksheet":
      return GROUP_TYPE_WORKSHEET;
    default:
      return ASSESSMENT;
  }
};

const styles = {
  alertBoxStyle: {
    borderTopLeftRadius: "0px",
    borderBottomLeftRadius: "0px",
    margin: "16px",
    marginBottom: "8px",
  },
  alertWarningStyle: {
    borderLeft: `2px solid ${colors.borderWarning}`,
  },
  alertErrorStyle: {
    borderLeft: `2px solid ${colors.borderCritical}`,
  },
  modalBoxStyle: {
    padding: "0px",
    paddingBottom: "8px",
  },
};

const ChangeTaskType = ({
  pageType,
  handleClose,
  curriculumType,
  getCurrentAssessmentTypes,
  assessmentTypeObj,
  changeAssessmentType,
  assessmentId,
  curriculumProgramId,
  organizationId,
  unitPlanId,
  getAssessmentObj,
  handleRightPane,
  assignmentId,
  title,
  assessmentTools = [],
  isLearningGoalsEvaluationEnabled = false,
  isFromGradebookAssignmentPopover,
  preSelectedTaskType,
  toastMsg = "",
  gradeId,
}) => {
  const { t } = useI18n();

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

  const { data, loading: isDataLoading } = useFetchQuery({
    query: getAssessmentLinkedUnitQuery,
    variables: {
      id: assessmentId,
    },
    skipCondition: !assessmentId || !isLearningCoursesEnabled,
  });
  const isData = !_.isEmpty(data);
  const showLoader = isLearningCoursesEnabled && !isData && isDataLoading;
  const fields = _.get(data, "node.fields", []);
  const unitPlanField = _.find(fields, { uid: "unitPlan" }) || {};
  const isUnitTagged = !_.isEmpty(unitPlanField?.value);

  const changeTaskTypeOptions = getChangeTaskTypeOptions({
    curriculumType,
    getCurrentAssessmentTypes,
    t,
    pageType,
    restrictQtIfUnitTagged: isLearningCoursesEnabled && isUnitTagged,
    curriculumId: curriculumProgramId,
    gradeId,
  });
  const currentAssessmentTypeValue = _.get(assessmentTypeObj, "value", null);
  const currentAssessmentTypeLabel = _.get(
    _.find(changeTaskTypeOptions, { value: currentAssessmentTypeValue }),
    "label",
    ""
  );

  const [selectedTaskType, setSelectedTaskType] = useState(
    preSelectedTaskType || currentAssessmentTypeValue
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const selectedAssessmentTypeLabel = _.get(
    _.find(changeTaskTypeOptions, { value: selectedTaskType }),
    "label",
    ""
  );

  const handleRadioButtonClick = selectedObj => {
    setSelectedTaskType(selectedObj.value);
  };

  const selectedOption = _.find(changeTaskTypeOptions, {
    value: selectedTaskType,
  });

  const onChangeClick = async () => {
    const groupType = getTypeEnum(selectedTaskType);

    const optionTemplateId = _.get(selectedOption, "templateId", null);

    // Use templateId from option if available, otherwise fallback to getTemplateIdFromOrgId
    const templateId =
      optionTemplateId ||
      getTemplateIdFromOrgId({
        organizationId,
        curriculumProgramId,
        groupType,
        assessmentType: selectedTaskType,
      });

    setIsLoading(true);

    await changeAssessmentType({
      id: assessmentId,
      assessmentType: selectedTaskType,
      type: groupType,
      templateId: templateId || undefined,
      currentAssessmentTypeGroup: getTypeEnum(currentAssessmentTypeValue),
      unitPlanId,
      pageType,
      selectedAssessmentTypeLabel,
      handleRightPane,
      assignmentId,
      toastMsg,
    });

    setIsLoading(false);
    handleClose();
  };

  const showNote = getShowNoteValue({
    currentAssessmentTypeValue,
    selectedTaskType,
    curriculumType,
  });

  const showCheckboxAlertMessage = getShowCheckboxAlertMessage({
    isLearningGoalsEvaluationEnabled,
    curriculumType,
    assessmentTools,
  });

  const disableChangeButton =
    currentAssessmentTypeValue === selectedTaskType ||
    ((selectedTaskType === "qt" ||
      selectedOption?.taskType === ASSESSMENT_TYPE_QUICK_TASK) &&
      !isChecked &&
      showCheckboxAlertMessage) ||
    showLoader;

  const handleCheckBoxChange = () => setIsChecked(prev => !prev);

  /**
   * Change without confirmation
   * - if coming from gradebook assignment popover and
   * - no note to show and not loading and
   * - preSelectedTaskType is present
   */
  const changeWithoutConfirmation =
    isFromGradebookAssignmentPopover &&
    !showNote &&
    !isDataLoading &&
    preSelectedTaskType;

  useEffect(() => {
    if (changeWithoutConfirmation) {
      onChangeClick();
    }
  }, [changeWithoutConfirmation]);

  const renderBody = () => {
    if (showLoader) {
      return (
        <div className={classes.loadingContainer}>
          <Loading />
        </div>
      );
    }

    return (
      <div className={classes.bodyContainer}>
        {!preSelectedTaskType &&
          _.map(changeTaskTypeOptions, option => {
            const { value, label } = option;

            const typeObj = getAssessmentObj({
              assessmentValue: value,
              contentType: ASSESSMENT,
            });

            const { avatarIconColor } = typeObj || {};

            return (
              <div
                key={value}
                onClick={() => handleRadioButtonClick(option)}
                className={classes.optionContainer}
              >
                <div className={classes.avatarContainer}>
                  <Avatar
                    shape={"square"}
                    shade={"light"}
                    size="small"
                    name={_.upperCase(label)}
                    color={avatarIconColor}
                    dsVersion={"2.0"}
                  />
                  <div className="text-body-2">{label}</div>
                </div>
                <RadioButton
                  checked={value === selectedTaskType}
                  dsVersion="2.0"
                />
              </div>
            );
          })}
        {selectedTaskType !== currentAssessmentTypeValue && showNote ? (
          <Alert
            type="warning"
            message={t("classRoom:change_task_type_note", {
              taskType1: selectedAssessmentTypeLabel,
              taskType2: currentAssessmentTypeLabel,
            })}
            style={{ ...styles.alertBoxStyle, ...styles.alertWarningStyle }}
            dsVersion="2.0"
          />
        ) : null}
        {selectedTaskType !== currentAssessmentTypeValue &&
        showNote &&
        (selectedTaskType === "qt" ||
          selectedOption?.taskType === ASSESSMENT_TYPE_QUICK_TASK) &&
        showCheckboxAlertMessage ? (
          <Alert
            type="error"
            variant="toast"
            title={t("classRoom:remove_evaluation_on_assessment_tools")}
            message={getCheckBoxAlertMessage({ curriculumType, t })}
            style={{ ...styles.alertBoxStyle, ...styles.alertErrorStyle }}
            dsVersion="2.0"
            isChecked={isChecked}
            onChange={handleCheckBoxChange}
          />
        ) : null}
      </div>
    );
  };

  return (
    <>
      {preSelectedTaskType && isDataLoading
        ? null
        : !changeWithoutConfirmation && (
            <Modal
              dsVersion="2.0"
              width="480px"
              height="max-content"
              onClose={handleClose}
            >
              <Modal.Header
                title={
                  preSelectedTaskType && selectedAssessmentTypeLabel
                    ? t("classRoom:change_task_type_to", {
                        taskName: title?.value
                          ? title.value
                          : t("common:untitled"),
                        taskType: selectedAssessmentTypeLabel,
                      })
                    : t("classRoom:change_task_type_for", {
                        taskName: title?.value
                          ? title.value
                          : t("common:untitled"),
                      })
                }
              />
              <Modal.Body style={styles.modalBoxStyle}>
                {renderBody()}
              </Modal.Body>
              <Modal.Footer>
                <div className={classes.footerContainer}>
                  <Button
                    type="outlined"
                    variant="neutral"
                    onClick={handleClose}
                    dsVersion="2.0"
                  >
                    {t("common:cancel")}
                  </Button>
                  <Button
                    disabled={disableChangeButton}
                    variant="destructive"
                    onClick={onChangeClick}
                    dsVersion="2.0"
                  >
                    {t("common:change")}
                  </Button>
                </div>
              </Modal.Footer>
            </Modal>
          )}
      {isLoading || (preSelectedTaskType && isDataLoading) ? (
        <FullScreenLoader />
      ) : null}
    </>
  );
};
const mapStateToProps = state => {
  const curriculumType = getCurrentCurriculumProgramType(state);
  const curriculumProgramId = getSelectedCurriculumId(state);
  const organizationId = getOrganizationId(state);
  const gradeId = _.get(state, "teacher.selected_class.selected_grades[0]", "");
  return {
    curriculumType,
    curriculumProgramId,
    organizationId,
    gradeId,
  };
};

const mapActionCreators = {
  getCurrentAssessmentTypes,
  changeAssessmentType,
  getAssessmentObj,
};

export default connect(mapStateToProps, mapActionCreators)(ChangeTaskType);
